import React from 'react';
import { useFormContext } from 'react-hook-form';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import SvgIcon from '@mui/material/SvgIcon';
import classNames from 'classnames';
import { useFeature } from 'flagged';
import { ReactComponent as AccordionToggle } from '@/assets/images/icons/accordion-toggle.svg';
import ControlledCheckbox from '@/components/common/form/ControlledCheckbox';
import ConstituentExportFieldName from '@/constants/constituents/ConstituentExportFieldName';
import FeatureFlags from '@/constants/system/FeatureFlags';
import translate from '@/lib/intl/translate';
import CsvExportBasicProfileFields from '../constants/CsvExportBasicProfileFields';
import CsvExportGroupFieldName from '../constants/CsvExportGroupFieldName';

export default function BasicProfileFields() {
  const hasHouseholdEnhancements = useFeature(
    FeatureFlags.HOUSEHOLD_ENHANCEMENTS,
  );
  const { control, setValue, watch } = useFormContext();
  const [canShowFields, setCanShowFields] = React.useState(false);

  const basicProfileCheckboxValue = watch(
    CsvExportGroupFieldName.BASIC_PROFILE,
  );

  React.useEffect(() => {
    CsvExportBasicProfileFields.forEach(({ fieldName }) =>
      setValue(fieldName, basicProfileCheckboxValue),
    );
  }, [basicProfileCheckboxValue]);

  const renderedFields = CsvExportBasicProfileFields.map(
    (field) =>
      field.fieldName !== ConstituentExportFieldName.CUSTOM_FIELDS && (
        <div key={field.fieldName}>
          <ControlledCheckbox
            control={control}
            label={field.fieldLabel}
            name={field.fieldName}
            data-testid={`constituent-export-dialog-${field.fieldName}-checkbox`}
            color="primary"
            id={`constituent-export-dialog-${field.fieldName}`}
          />
        </div>
      ),
  );

  return hasHouseholdEnhancements ? (
    <div>
      <div className="flex justify-between py-2">
        <ControlledCheckbox
          color="primary"
          label={
            <div className="ml-3">
              <h3 className="text-text-primary type-body2">
                {translate('CONSTITUENT_EXPORT_BASIC_PROFILE')}
              </h3>
              <div className="mt-1 text-text-secondary type-caption1">
                {translate('CONSTITUENT_EXPORT_BASIC_PROFILE_DESCRIPTION')}
              </div>
            </div>
          }
          name={CsvExportGroupFieldName.BASIC_PROFILE}
        />
        <IconButton
          aria-label={translate('CONSTITUENT_EXPORT_BASIC_PROFILE_TOGGLE')}
          aria-pressed={canShowFields}
          className={classNames(
            'bg-transparent text-tertiary-main hover:bg-transparent focus-visible:[outline:auto]',
            {
              'rotate-180': !canShowFields,
            },
          )}
          data-testid="constituent-export-basic-profile-expand-toggle-button"
          onClick={() => {
            setCanShowFields((prev) => !prev);
          }}
        >
          <SvgIcon
            aria-label={
              canShowFields
                ? translate('ICON_EXPAND')
                : translate('ICON_COLLAPSE')
            }
            component={AccordionToggle}
            width="24"
            height="24"
          />
        </IconButton>
      </div>
      <Collapse in={canShowFields}>
        <div className="p-4 md:columns-2">{renderedFields}</div>
      </Collapse>
    </div>
  ) : (
    <div className="md:columns-2">{renderedFields}</div>
  );
}
